import React, { useState, useEffect, useCallback } from "react";
import AddPharmacyItem from "./AddPharmacyItems";
import { getPharmacyItems, getPharmacyItemById, updatePharmacyItem, getPharmacyItemBatches, getPharmacyItemStockMovements } from "../../apis/pharmacyItems";
import { getCompanies } from "../../apis/company";
import { Button, DatePicker, Dropdown, Modal, Select, Table, Tabs, Tag, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { MedicineBoxOutlined } from "@ant-design/icons";
import { BeakerIcon } from "@heroicons/react/24/solid";
import dayjs from "dayjs";
import CommonPagination from "../../components/CommanPagination";
import SearchBar from "../../components/SearchBar";

// ─── Constants ────────────────────────────────────────────────────────────────

const { RangePicker } = DatePicker;

const MOVEMENT_TYPE_COLORS: Record<string, string> = {
  grn: "blue",
  sale: "red",
  return: "green",
  internal: "purple",
  adjustment: "orange",
  transfer: "cyan",
  expired: "default",
  damaged: "magenta",
};
const ITEM_TYPE_TABS = [
  { value: "", label: "All" },
  { value: "medicine", label: "Medicine" },
  { value: "surgical", label: "Surgical" },
  { value: "implant", label: "Implant" },
];

const VIEW_TABS = [
  { value: "items", label: "All" },
  { value: "expiring_soon", label: "Expiring Soon" },
  { value: "expired", label: "Expired" },
];

const typeTagColor: Record<string, string> = {
  medicine: "#3b82f6",
  surgical: "#8b5cf6",
  implant: "#06b6d4",
};

// Today's reference date
const TODAY = new Date();

// ─── Helpers ──────────────────────────────────────────────────────────────────

const { Option } = Select;

const fmt = (n: number) => Number(n ?? 0).toLocaleString();

const stockColor = (qty: number) =>
  qty === 0 ? "red" : qty <= 10 ? "orange" : "green";

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** Days between two dates (positive = future, negative = past). */
const daysDiff = (dateStr: string | null | undefined): number => {
  if (!dateStr) return 0;
  const target = new Date(dateStr);
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((target.getTime() - TODAY.getTime()) / msPerDay);
};

const normalizeBatch = (batch: any) => {
  const qty = Number(
    batch.availableQuantity ??
      batch.available_quantity ??
      batch.currentQuantity ??
      batch.current_quantity ??
      0,
  );
  return {
    id: batch.id,
    batchNumber: batch.batchNumber ?? batch.batch_number ?? "—",
    manufacturingDate: batch.manufacturingDate ?? batch.manufacturing_date ?? null,
    expiryDate: batch.expiryDate ?? batch.expiry_date ?? null,
    purchasePrice: batch.purchasePrice ?? batch.purchase_price ?? "0",
    sellingPrice: batch.sellingPrice ?? batch.selling_price ?? "0",
    availableQuantity: qty,
    isExpired: batch.isExpired ?? batch.is_expired ?? false,
    isExpiringSoon: batch.isExpiringSoon ?? batch.is_expiring_soon ?? false,
    supplierName:
      batch.supplier?.name ??
      batch.supplierName ??
      null,
    notes: batch.notes ?? null,
    isActive: batch.isActive ?? batch.is_active ?? true,
    taxPercentage: batch.taxPercentage ?? batch.tax_percentage ?? null,
    taxAmount: batch.taxAmount ?? batch.tax_amount ?? null,
    discountPercentage: batch.discountPercentage ?? batch.discount_percentage ?? null,
    discountAmount: batch.discountAmount ?? batch.discount_amount ?? null,
  };
};

// ─── BatchTable (used in expandable row + batches modal) ──────────────────────

const BatchTable: React.FC<{ batches: any[] }> = ({ batches }) => {
  const cols: ColumnsType<any> = [
    {
      title: "Batch No",
      dataIndex: "batchNumber",
      key: "batchNumber",
      render: (v: string) => <span className="font-mono text-xs font-semibold">{v || "—"}</span>,
    },
    {
      title: "Mfg Date",
      dataIndex: "manufacturingDate",
      key: "manufacturingDate",
      render: (v: string) => <span className="text-xs text-gray-600">{formatDate(v)}</span>,
    },
    {
      title: "Expiry",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (v: string, row: any) => {
        if (!v) return <span className="text-gray-400">—</span>;
        const label = formatDate(v);
        if (row.isExpired) return <Tag color="red">{label} · Expired</Tag>;
        if (row.isExpiringSoon) return <Tag color="orange">{label} · Expiring Soon</Tag>;
        return <span className="text-gray-700 text-xs">{label}</span>;
      },
    },
    {
      title: "Qty",
      dataIndex: "availableQuantity",
      key: "availableQuantity",
      align: "right" as const,
      render: (v: number) => (
        <span className="font-semibold tabular-nums" style={{ color: stockColor(v ?? 0) }}>
          {fmt(v ?? 0)}
        </span>
      ),
    },
    {
      title: "MRP (₹)",
      key: "mrp",
      align: "right" as const,
      render: (_: any, row: any) => {
        const mrp = parseFloat(row.sellingPrice ?? "0");
        return <span className="tabular-nums text-xs">₹{mrp.toFixed(2)}</span>;
      },
    },
    {
      title: "Purchase (₹)",
      key: "purchase",
      align: "right" as const,
      render: (_: any, row: any) => {
        const p = parseFloat(row.purchasePrice ?? "0");
        return <span className="tabular-nums text-xs text-gray-500">₹{p.toFixed(2)}</span>;
      },
    },
    {
      title: "Tax %",
      dataIndex: "taxPercentage",
      key: "tax",
      align: "right" as const,
      render: (v: string | number | null | undefined) => (v != null ? `${v}%` : "—"),
    },
    {
      title: "Supplier",
      key: "supplier",
      render: (_: any, row: any) => (
        <span className="text-xs text-gray-600">{row.supplierName || "—"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (v: boolean) => (
        <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>
      ),
    },
  ];

  if (!batches.length) {
    return <p className="text-sm text-gray-500 py-4 text-center">No batches found for this item.</p>;
  }

  return (
    <Table
      rowKey="id"
      size="small"
      pagination={false}
      dataSource={batches}
      columns={cols}
      scroll={{ x: 900 }}
      rowClassName={(row: any) =>
        row.isExpired ? "bg-red-50" : row.isExpiringSoon ? "bg-orange-50" : ""
      }
    />
  );
};

// ─── Flat batch row type (for expiry views) ───────────────────────────────────

interface FlatBatchRow {
  key: string;
  itemName: string;
  itemSku: string | null;
  companyName: string;
  batchNumber: string;
  availableQuantity: number;
  expiryDate: string | null;
  sellingPrice: string;
  isExpired: boolean;
  isExpiringSoon: boolean;
}

// ─── Main component ───────────────────────────────────────────────────────────

const PharmacyItemsList = () => {
  // ── Modal state ──
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editableItem, setEditableItem] = useState<any>(null);
  const [batchesModalItem, setBatchesModalItem] = useState<any | null>(null);
  const [batchesModalRows, setBatchesModalRows] = useState<any[]>([]);
  const [batchesModalLoading, setBatchesModalLoading] = useState(false);

  const [stockModalItem, setStockModalItem] = useState<any | null>(null);
  const [stockRows, setStockRows] = useState<any[]>([]);
  const [stockSummary, setStockSummary] = useState<any>(null);
  const [stockMeta, setStockMeta] = useState<any>(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockPage, setStockPage] = useState(1);
  const [stockTypeFilter, setStockTypeFilter] = useState<string>("");
  const [stockDirectionFilter, setStockDirectionFilter] = useState<string>("");
  const [stockDateRange, setStockDateRange] = useState<[string, string] | null>(null);

  // ── View tab: "items" | "expiring_soon" | "expired" ──
  const [viewTab, setViewTab] = useState<"items" | "expiring_soon" | "expired">("items");

  // ── Main Items view state ──
  const [pharmacyItems, setPharmacyItems] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({
    name: "",
    company: "",
    status: "",
    expiryStatus: "",
    stockStatus: "",
    itemType: "",
  });
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);

  // ── Expiry views state ──
  const [expiryRows, setExpiryRows] = useState<FlatBatchRow[]>([]);
  const [expiryLoading, setExpiryLoading] = useState(false);

  // ── Load companies once ──
  useEffect(() => {
    getCompanies({})
      .then((res: any) => {
        const data = res?.data ?? [];
        setCompanyOptions(data.map((c: any) => ({ value: c.name, label: c.name })));
      })
      .catch(() => {});
  }, []);

  // ── Fetch main items list ──
  const fetchWithFilters = useCallback(
    (overrides: Record<string, unknown> = {}) => {
      const params: Record<string, unknown> = {
        page: overrides.page ?? page,
        per_page: overrides.per_page ?? pageSize,
      };
      if (filters.name.trim()) params.name = filters.name.trim();
      if (filters.company.trim()) params.company = filters.company.trim();
      if (filters.status === "active") params.is_active = true;
      if (filters.status === "inactive") params.is_active = false;
      if (filters.expiryStatus) params.expiry_status = filters.expiryStatus;
      if (filters.stockStatus) params.stock_status = filters.stockStatus;
      if (filters.itemType) params.item_type = filters.itemType;

      setLoading(true);
      getPharmacyItems(params)
        .then((res: any) => {
          setPharmacyItems(res.data || []);
          setMeta(res.meta);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    },
    [filters, page, pageSize],
  );

  useEffect(() => {
    if (viewTab !== "items") return;
    const t = setTimeout(() => fetchWithFilters({ page: 1 }), 300);
    return () => clearTimeout(t);
  }, [filters, viewTab]);

  // ── Fetch expiry / expired data ──
  const fetchExpiryData = useCallback(
    (status: "expiring_soon" | "expired") => {
      setExpiryLoading(true);
      getPharmacyItems({ expiry_status: status, per_page: 200 })
        .then((res: any) => {
          const items: any[] = res?.data ?? [];
          const rows: FlatBatchRow[] = [];
          items.forEach((item: any) => {
            const batches: any[] = item.batches ?? [];
            batches.forEach((batch: any) => {
              const isExpired: boolean = batch.isExpired ?? batch.is_expired ?? false;
              const isExpiringSoon: boolean = batch.isExpiringSoon ?? batch.is_expiring_soon ?? false;
              const qty = Number(batch.availableQuantity ?? batch.available_quantity ?? 0);
              // For expiring_soon: include batches that are expiring soon
              // For expired: include batches that are expired (with any qty, to show depletion state)
              if (status === "expiring_soon" && !isExpiringSoon) return;
              if (status === "expired" && !isExpired) return;
              rows.push({
                key: `${item.id}-${batch.id}`,
                itemName: item.name ?? "—",
                itemSku: item.sku ?? null,
                companyName: item.company?.name ?? "—",
                batchNumber: batch.batchNumber ?? batch.batch_number ?? "—",
                availableQuantity: qty,
                expiryDate: batch.expiryDate ?? batch.expiry_date ?? null,
                sellingPrice: batch.sellingPrice ?? batch.selling_price ?? "0",
                isExpired,
                isExpiringSoon,
              });
            });
          });
          setExpiryRows(rows);
        })
        .catch(console.error)
        .finally(() => setExpiryLoading(false));
    },
    [],
  );

  useEffect(() => {
    if (viewTab === "expiring_soon") fetchExpiryData("expiring_soon");
    if (viewTab === "expired") fetchExpiryData("expired");
  }, [viewTab, fetchExpiryData]);

  // ── Helpers ──
  const setFilter = (key: keyof typeof filters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleEditItem = (item: any) => {
    getPharmacyItemById(item.id)
      .then((res: any) => {
        setEditableItem(res);
        setIsModalOpen(true);
      })
      .catch(console.error);
  };

  const openBatchesModal = (item: any) => {
    setBatchesModalItem(item);
    setBatchesModalRows((item.batches ?? []).map(normalizeBatch));
    setBatchesModalLoading(true);
    getPharmacyItemBatches(item.id)
      .then((res: any) => {
        const batches = res?.batches ?? res?.data?.batches ?? [];
        setBatchesModalRows(batches.map(normalizeBatch));
      })
      .catch(() => {
        // Keep list-embedded batches if refresh fails
      })
      .finally(() => setBatchesModalLoading(false));
  };

  const closeBatchesModal = () => {
    setBatchesModalItem(null);
    setBatchesModalRows([]);
    setBatchesModalLoading(false);
  };

  const fetchStockMovements = (
    itemId: number,
    overrides: Record<string, unknown> = {}
  ) => {
    setStockLoading(true);
    const params: Record<string, unknown> = {
      page: overrides.page ?? stockPage,
      per_page: 50,
    };
    const type = overrides.movement_type !== undefined ? overrides.movement_type : stockTypeFilter;
    const direction =
      overrides.direction !== undefined ? overrides.direction : stockDirectionFilter;
    const range =
      overrides.date_range !== undefined
        ? (overrides.date_range as [string, string] | null)
        : stockDateRange;
    if (type) params.movement_type = type;
    if (direction) params.direction = direction;
    if (range?.[0]) params.date_from = range[0];
    if (range?.[1]) params.date_to = range[1];

    getPharmacyItemStockMovements(itemId, params)
      .then((res: any) => {
        setStockRows(res?.data ?? []);
        setStockSummary(res?.summary ?? null);
        setStockMeta(res?.meta ?? null);
      })
      .catch(() => {
        message.error("Failed to load stock movements");
        setStockRows([]);
      })
      .finally(() => setStockLoading(false));
  };

  const openStockMovementsModal = (item: any) => {
    setStockModalItem(item);
    setStockPage(1);
    setStockTypeFilter("");
    setStockDirectionFilter("");
    setStockDateRange(null);
    setStockRows([]);
    setStockSummary(null);
    fetchStockMovements(item.id, {
      page: 1,
      movement_type: "",
      direction: "",
      date_range: null,
    });
  };

  const closeStockMovementsModal = () => {
    setStockModalItem(null);
    setStockRows([]);
    setStockSummary(null);
    setStockMeta(null);
  };

  const handleToggleConsumable = (item: any) => {
    updatePharmacyItem(item.id, { is_consumable: !item.isConsumable }).then(() =>
      setPharmacyItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, isConsumable: !i.isConsumable } : i)),
      ),
    );
  };

  const handleToggleActive = (item: any) => {
    updatePharmacyItem(item.id, { is_active: !item.isActive }).then(() =>
      setPharmacyItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, isActive: !i.isActive } : i)),
      ),
    );
  };

  const onPaginationChange = (p: number, ps?: number) => {
    setPage(p);
    setPageSize(ps ?? pageSize);
    fetchWithFilters({ page: p, per_page: ps ?? pageSize });
  };

  // ── Resolve MRP: selling_price from the batch with nearest non-expired expiry ──
  const resolveMrp = (batches: any[]): string => {
    const active = (batches ?? []).filter(
      (b: any) => !(b.isExpired ?? b.is_expired ?? false) && (b.isActive ?? b.is_active ?? true),
    );
    if (active.length === 0) {
      // Fall back to first batch
      const first = (batches ?? [])[0];
      if (!first) return "—";
      const p = parseFloat(first.sellingPrice ?? first.selling_price ?? "0");
      return `₹${p.toFixed(2)}`;
    }
    // Sort by nearest expiry date
    const sorted = [...active].sort((a: any, b: any) => {
      const da = a.expiryDate ?? a.expiry_date;
      const db = b.expiryDate ?? b.expiry_date;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return new Date(da).getTime() - new Date(db).getTime();
    });
    const p = parseFloat(sorted[0].sellingPrice ?? sorted[0].selling_price ?? "0");
    return `₹${p.toFixed(2)}`;
  };

  // ── Main Items columns ──
  const columns: ColumnsType<any> = [
    {
      title: "Item",
      key: "name",
      width: 240,
      render: (_: any, item: any) => (
        <div className="flex items-center gap-2">
          <Tooltip title={item.isConsumable ? "Consumable" : "Non-Consumable"}>
            {item.isConsumable ? (
              <BeakerIcon
                className="h-5 w-5 text-gray-500 flex-shrink-0 cursor-pointer"
                onClick={() => handleToggleConsumable(item)}
              />
            ) : (
              <MedicineBoxOutlined
                style={{ fontSize: 16, color: "#6b7280", cursor: "pointer" }}
                onClick={() => handleToggleConsumable(item)}
              />
            )}
          </Tooltip>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-gray-900 text-sm">{item.name}</span>
              {item.itemType && item.itemType !== "medicine" && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded font-medium text-white"
                  style={{
                    backgroundColor: typeTagColor[item.itemType] ?? "#6b7280",
                    fontSize: 10,
                  }}
                >
                  {item.itemType}
                </span>
              )}
            </div>
            {item.sku && <div className="text-xs text-gray-400">{item.sku}</div>}
          </div>
        </div>
      ),
    },
    {
      title: "Company",
      key: "company",
      width: 140,
      render: (_: any, item: any) => (
        <span className="text-sm text-gray-700">{item.company?.name ?? "—"}</span>
      ),
    },
    {
      title: "Form / Pack",
      key: "form",
      width: 150,
      render: (_: any, item: any) => (
        <span className="text-sm text-gray-700">
          {[item.form, item.strength, item.unit].filter(Boolean).join(" · ") || "—"}
        </span>
      ),
    },
    {
      title: "Stock",
      key: "stock",
      width: 100,
      align: "right" as const,
      render: (_: any, item: any) => {
        const batches: any[] = (item.batches ?? []).map(normalizeBatch);
        const activeQty = batches
          .filter((b: any) => !b.isExpired && b.isActive !== false)
          .reduce((s: number, b: any) => s + Number(b.availableQuantity ?? 0), 0);
        const hasBatches = batches.length > 0;
        return (
          <button
            type="button"
            disabled={!hasBatches}
            onClick={() => hasBatches && openBatchesModal(item)}
            className={`text-base font-bold tabular-nums ${
              hasBatches ? "hover:underline cursor-pointer" : "cursor-default"
            }`}
            style={{ color: stockColor(activeQty) }}
            title={hasBatches ? "View batch-wise stock" : undefined}
          >
            {fmt(activeQty)}
          </button>
        );
      },
    },
    {
      title: "MRP",
      key: "mrp",
      width: 100,
      align: "right" as const,
      render: (_: any, item: any) => (
        <span className="text-sm tabular-nums text-gray-800">
          {resolveMrp(item.batches ?? [])}
        </span>
      ),
    },
    {
      title: "Expiry Alert",
      key: "expiryAlert",
      width: 160,
      render: (_: any, item: any) => {
        const batches: any[] = item.batches ?? [];
        const hasExpiredStock = batches.some(
          (b: any) => (b.isExpired ?? false) && Number(b.availableQuantity ?? 0) > 0,
        );
        const hasExpiringSoon = batches.some(
          (b: any) =>
            (b.isExpiringSoon ?? false) &&
            !(b.isExpired ?? false) &&
            Number(b.availableQuantity ?? 0) > 0,
        );
        if (hasExpiredStock) return <Tag color="red">Expired Stock</Tag>;
        if (hasExpiringSoon) return <Tag color="orange">Expiring Soon</Tag>;
        return null;
      },
    },
    {
      title: "Batches",
      key: "batchCount",
      width: 100,
      align: "center" as const,
      render: (_: any, item: any) => {
        const count = (item.batches ?? []).length;
        if (count === 0) {
          return <span className="text-xs text-gray-400">—</span>;
        }
        return (
          <button
            type="button"
            onClick={() => openBatchesModal(item)}
            className="text-xs font-semibold text-primary tabular-nums hover:underline cursor-pointer"
            title="View batch details"
          >
            {count} batch{count !== 1 ? "es" : ""}
          </button>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 90,
      render: (_: any, item: any) => (
        <Tag
          color={item.isActive ? "green" : "red"}
          onClick={() => handleToggleActive(item)}
          className="cursor-pointer"
        >
          {item.isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_: any, item: any) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: "Edit",
                onClick: () => handleEditItem(item),
              },
              {
                key: "stock-movements",
                label: "View stock movement",
                onClick: () => openStockMovementsModal(item),
              },
            ],
          }}
          placement="bottomRight"
        >
          <Button size="small">Actions</Button>
        </Dropdown>
      ),
    },
  ];

  // ── Flat batch columns for Expiring Soon / Expired tabs ──
  const expiryColumns = (mode: "expiring_soon" | "expired"): ColumnsType<FlatBatchRow> => [
    {
      title: "Item",
      key: "item",
      width: 220,
      render: (_: any, row: FlatBatchRow) => (
        <div>
          <div className="font-medium text-gray-900 text-sm">{row.itemName}</div>
          {row.itemSku && <div className="text-xs text-gray-400">{row.itemSku}</div>}
        </div>
      ),
    },
    {
      title: "Company",
      key: "company",
      width: 130,
      render: (_: any, row: FlatBatchRow) => (
        <span className="text-sm text-gray-700">{row.companyName}</span>
      ),
    },
    {
      title: "Batch No",
      key: "batchNumber",
      width: 130,
      render: (_: any, row: FlatBatchRow) => (
        <span className="font-mono text-xs">{row.batchNumber}</span>
      ),
    },
    {
      title: "Available Qty",
      key: "qty",
      width: 110,
      align: "right" as const,
      render: (_: any, row: FlatBatchRow) => (
        <span
          className="font-semibold tabular-nums"
          style={{ color: stockColor(row.availableQuantity) }}
        >
          {fmt(row.availableQuantity)}
        </span>
      ),
    },
    {
      title: "Expiry Date",
      key: "expiryDate",
      width: 180,
      render: (_: any, row: FlatBatchRow) => (
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-700">{formatDate(row.expiryDate)}</span>
          {mode === "expired" ? (
            <Tag color="red">Expired</Tag>
          ) : (
            <Tag color="orange">Expiring Soon</Tag>
          )}
        </div>
      ),
    },
    {
      title: mode === "expired" ? "Expired" : "Days Left",
      key: "daysLeft",
      width: 130,
      align: "right" as const,
      render: (_: any, row: FlatBatchRow) => {
        const diff = daysDiff(row.expiryDate);
        if (mode === "expired") {
          const ago = Math.abs(diff);
          return (
            <span className="text-xs text-red-600 font-medium">
              {ago === 0 ? "Today" : `Expired ${ago} day${ago !== 1 ? "s" : ""} ago`}
            </span>
          );
        }
        return (
          <span
            className="text-xs font-medium tabular-nums"
            style={{ color: diff <= 7 ? "red" : diff <= 30 ? "orange" : "inherit" }}
          >
            {diff} day{diff !== 1 ? "s" : ""}
          </span>
        );
      },
    },
    {
      title: "MRP (₹)",
      key: "mrp",
      width: 100,
      align: "right" as const,
      render: (_: any, row: FlatBatchRow) => {
        const p = parseFloat(row.sellingPrice || "0");
        return <span className="tabular-nums text-sm">₹{p.toFixed(2)}</span>;
      },
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-base font-semibold text-gray-900">Pharmacy Items</h1>
          {viewTab === "items" && meta?.total != null && (
            <p className="text-sm text-gray-500 mt-0.5">{meta.total} items</p>
          )}
        </div>
        <button
          onClick={() => {
            setEditableItem(null);
            setIsModalOpen(true);
          }}
          className="px-3 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover"
        >
          Add Pharmacy Item
        </button>
      </div>

      <Tabs
        activeKey={viewTab}
        onChange={(key) => setViewTab(key as typeof viewTab)}
        items={[
          {
            key: "items",
            label: "All",
            children: (
              <>
                {/* Item Type sub-tabs */}
                <div className="flex gap-2 mb-3">
                  {ITEM_TYPE_TABS.map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setFilter("itemType", tab.value)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        filters.itemType === tab.value
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Filters */}
                <div className="mb-3">
                  <SearchBar
                    placeholder="Search item name..."
                    onSearch={(v) => setFilter("name", v)}
                  >
                    <Select
                      placeholder="Company"
                      allowClear
                      style={{ width: 180 }}
                      options={companyOptions}
                      value={filters.company || undefined}
                      onChange={(v) => setFilter("company", v ?? "")}
                    />
                    <Select
                      placeholder="Status"
                      value={filters.status || undefined}
                      onChange={(v) => setFilter("status", v ?? "")}
                      allowClear
                      style={{ width: 120 }}
                    >
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                    <Select
                      placeholder="Expiry"
                      value={filters.expiryStatus || undefined}
                      onChange={(v) => setFilter("expiryStatus", v ?? "")}
                      allowClear
                      style={{ width: 160 }}
                    >
                      <Option value="expired">Has Expired Stock</Option>
                      <Option value="expiring_soon">Expiring Soon</Option>
                    </Select>
                    <Select
                      placeholder="Stock"
                      value={filters.stockStatus || undefined}
                      onChange={(v) => setFilter("stockStatus", v ?? "")}
                      allowClear
                      style={{ width: 140 }}
                    >
                      <Option value="out_of_stock">Out of Stock</Option>
                      <Option value="low_stock">Low Stock (≤10)</Option>
                    </Select>
                  </SearchBar>
                </div>

                <Table
                  rowKey="id"
                  size="small"
                  loading={loading}
                  dataSource={pharmacyItems}
                  columns={columns}
                  pagination={false}
                  expandable={{
                    rowExpandable: (record) => (record?.batches?.length ?? 0) > 0,
                    expandedRowRender: (record) => (
                      <div className="py-2 px-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Batches — {record.name}
                          </p>
                          <button
                            type="button"
                            className="text-xs text-primary hover:underline"
                            onClick={() => openBatchesModal(record)}
                          >
                            Open full details
                          </button>
                        </div>
                        <BatchTable batches={(record.batches ?? []).map(normalizeBatch)} />
                      </div>
                    ),
                    expandRowByClick: false,
                  }}
                  locale={{ emptyText: "No pharmacy items found." }}
                />
                <CommonPagination meta={meta} onChange={onPaginationChange} />
              </>
            ),
          },
          {
            key: "expiring_soon",
            label: "Expiring Soon",
            children: (
              <Table
                rowKey="key"
                size="small"
                loading={expiryLoading}
                dataSource={expiryRows}
                columns={expiryColumns("expiring_soon")}
                pagination={{ pageSize: 50, showSizeChanger: false }}
                locale={{ emptyText: "No batches expiring soon." }}
              />
            ),
          },
          {
            key: "expired",
            label: "Expired",
            children: (
              <Table
                rowKey="key"
                size="small"
                loading={expiryLoading}
                dataSource={expiryRows}
                columns={expiryColumns("expired")}
                pagination={{ pageSize: 50, showSizeChanger: false }}
                locale={{ emptyText: "No expired batches found." }}
              />
            ),
          },
        ]}
      />

      {/* Batches detail modal */}
      <Modal
        title={
          batchesModalItem
            ? `Batches — ${batchesModalItem.name}${
                batchesModalItem.sku ? ` (${batchesModalItem.sku})` : ""
              }`
            : "Batches"
        }
        open={Boolean(batchesModalItem)}
        onCancel={closeBatchesModal}
        footer={null}
        width={960}
        destroyOnClose
      >
        <div className="mb-3 flex flex-wrap gap-3 text-sm text-gray-600">
          <span>
            Company:{" "}
            <strong className="text-gray-900">
              {batchesModalItem?.company?.name ?? "—"}
            </strong>
          </span>
          <span>
            Total batches:{" "}
            <strong className="text-gray-900">{batchesModalRows.length}</strong>
          </span>
          <span>
            Active qty:{" "}
            <strong
              style={{
                color: stockColor(
                  batchesModalRows
                    .filter((b) => !b.isExpired && b.isActive !== false)
                    .reduce((s, b) => s + Number(b.availableQuantity ?? 0), 0),
                ),
              }}
            >
              {fmt(
                batchesModalRows
                  .filter((b) => !b.isExpired && b.isActive !== false)
                  .reduce((s, b) => s + Number(b.availableQuantity ?? 0), 0),
              )}
            </strong>
          </span>
        </div>
        {batchesModalLoading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading batches…</div>
        ) : (
          <BatchTable batches={batchesModalRows} />
        )}
      </Modal>

      {/* Stock movements ledger */}
      <Modal
        title={
          stockModalItem
            ? `Stock Movement — ${stockModalItem.name}${
                stockModalItem.sku ? ` (${stockModalItem.sku})` : ""
              }`
            : "Stock Movement"
        }
        open={Boolean(stockModalItem)}
        onCancel={closeStockMovementsModal}
        footer={null}
        width={1100}
        destroyOnClose
      >
        {stockSummary && (
          <div className="mb-4 grid grid-cols-3 gap-3">
            <div className="rounded-md border border-green-100 bg-green-50 px-3 py-2">
              <div className="text-xs text-green-700">Stock In</div>
              <div className="text-lg font-semibold text-green-800 tabular-nums">
                +{fmt(stockSummary.stockIn ?? stockSummary.stock_in ?? 0)}
              </div>
            </div>
            <div className="rounded-md border border-red-100 bg-red-50 px-3 py-2">
              <div className="text-xs text-red-700">Stock Out</div>
              <div className="text-lg font-semibold text-red-800 tabular-nums">
                -{fmt(stockSummary.stockOut ?? stockSummary.stock_out ?? 0)}
              </div>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-xs text-gray-600">Balance</div>
              <div
                className="text-lg font-semibold tabular-nums"
                style={{
                  color: stockColor(Number(stockSummary.balance ?? 0)),
                }}
              >
                {fmt(stockSummary.balance ?? 0)}
              </div>
            </div>
          </div>
        )}

        <div className="mb-3 flex flex-wrap gap-2">
          <Select
            allowClear
            size="small"
            placeholder="Movement type"
            style={{ width: 200 }}
            value={stockTypeFilter || undefined}
            onChange={(v) => {
              const next = v ?? "";
              setStockTypeFilter(next);
              setStockPage(1);
              if (stockModalItem) {
                fetchStockMovements(stockModalItem.id, {
                  page: 1,
                  movement_type: next,
                });
              }
            }}
            options={[
              { value: "grn", label: "GRN" },
              { value: "sale", label: "Pharmacy Sale" },
              { value: "return", label: "Credit Note / Sale Return" },
              { value: "internal", label: "Internal Stock Issue" },
              { value: "adjustment", label: "Adjustment" },
              { value: "transfer", label: "Transfer" },
              { value: "expired", label: "Expired" },
              { value: "damaged", label: "Damaged" },
            ]}
          />
          <Select
            allowClear
            size="small"
            placeholder="Direction"
            style={{ width: 120 }}
            value={stockDirectionFilter || undefined}
            onChange={(v) => {
              const next = v ?? "";
              setStockDirectionFilter(next);
              setStockPage(1);
              if (stockModalItem) {
                fetchStockMovements(stockModalItem.id, {
                  page: 1,
                  direction: next,
                });
              }
            }}
            options={[
              { value: "in", label: "In" },
              { value: "out", label: "Out" },
            ]}
          />
          <RangePicker
            size="small"
            format="YYYY-MM-DD"
            onChange={(_, strings) => {
              const range =
                strings[0] && strings[1]
                  ? ([strings[0], strings[1]] as [string, string])
                  : null;
              setStockDateRange(range);
              setStockPage(1);
              if (stockModalItem) {
                fetchStockMovements(stockModalItem.id, {
                  page: 1,
                  date_range: range,
                });
              }
            }}
          />
        </div>

        <Table
          rowKey="id"
          size="small"
          loading={stockLoading}
          dataSource={stockRows}
          pagination={false}
          scroll={{ x: 980 }}
          locale={{ emptyText: "No stock movements found for this item." }}
          columns={[
            {
              title: "Date",
              dataIndex: "movementDate",
              key: "movementDate",
              width: 150,
              render: (v: string) =>
                v ? dayjs(v).format("DD MMM YYYY, h:mm a") : "—",
            },
            {
              title: "Type",
              dataIndex: "movementType",
              key: "movementType",
              width: 160,
              render: (v: string, row: any) => (
                <Tag color={MOVEMENT_TYPE_COLORS[v] ?? "default"}>
                  {row.movementTypeLabel || v || "—"}
                </Tag>
              ),
            },
            {
              title: "Direction",
              dataIndex: "direction",
              key: "direction",
              width: 90,
              render: (v: string) => (
                <Tag color={v === "in" ? "green" : "red"}>
                  {v === "in" ? "IN" : "OUT"}
                </Tag>
              ),
            },
            {
              title: "Qty",
              dataIndex: "signedQuantity",
              key: "signedQuantity",
              width: 90,
              align: "right" as const,
              render: (v: number, row: any) => {
                const qty = v ?? (row.direction === "in" ? row.quantity : -row.quantity);
                return (
                  <span
                    className={`tabular-nums font-medium ${
                      Number(qty) >= 0 ? "text-green-700" : "text-red-700"
                    }`}
                  >
                    {Number(qty) > 0 ? `+${fmt(qty)}` : fmt(qty)}
                  </span>
                );
              },
            },
            {
              title: "Batch",
              dataIndex: "batchNumber",
              key: "batchNumber",
              width: 120,
              render: (v: string) => (
                <span className="font-mono text-xs">{v || "—"}</span>
              ),
            },
            {
              title: "Reference",
              key: "reference",
              width: 180,
              render: (_: any, row: any) => (
                <div>
                  <div className="font-mono text-xs text-gray-900">
                    {row.referenceNumber || "—"}
                  </div>
                  {row.creditNoteNo && (
                    <div className="text-xs text-gray-500">CN: {row.creditNoteNo}</div>
                  )}
                </div>
              ),
            },
            {
              title: "Notes",
              dataIndex: "notes",
              key: "notes",
              ellipsis: true,
              render: (v: string) => (
                <span className="text-xs text-gray-500">{v || "—"}</span>
              ),
            },
            {
              title: "By",
              dataIndex: "createdBy",
              key: "createdBy",
              width: 120,
              render: (v: string) => (
                <span className="text-xs text-gray-600">{v || "—"}</span>
              ),
            },
          ]}
        />
        <CommonPagination
          meta={stockMeta}
          onChange={(p: number) => {
            setStockPage(p);
            if (stockModalItem) fetchStockMovements(stockModalItem.id, { page: p });
          }}
        />
      </Modal>

      {/* Add / Edit modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[96vh] overflow-y-auto relative">
            <div className="flex items-center justify-between bg-primary text-white px-4 py-3 rounded-t-lg">
              <h2 className="text-lg font-semibold">
                {editableItem ? "Update Pharmacy Item" : "Add Pharmacy Item"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-white hover:text-gray-200 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <AddPharmacyItem
                closeModal={() => {
                  setIsModalOpen(false);
                  fetchWithFilters({ page, per_page: pageSize });
                  setEditableItem(null);
                }}
                editableItem={editableItem}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyItemsList;
